import { Directive, DoCheck, Input, OnDestroy, OnInit } from '@angular/core';
import { Table } from 'primeng/table';
import { Subscription } from 'rxjs';
import { UrlStateService } from '../../url-state/url-state.service';
import { UrlParamDefinition } from '../../url-state/url-state.types';

@Directive({ selector: 'p-table[ssTableUrlState]', standalone: false })
export class TableUrlStateDirective implements OnInit, DoCheck, OnDestroy {
  @Input() ssTableUrlState = '';
  @Input() urlSortFields: Record<string, string> = {};
  private subscriptions = new Subscription();
  private defaultField?: string;
  private defaultDirection = 1;
  private previousValue: unknown;
  private restoring = true;
  private destroyed = false;

  constructor(private table: Table, private urlState: UrlStateService) { }

  ngOnInit(): void {
    this.defaultField = typeof this.table.sortField === 'string' ? this.table.sortField : undefined;
    this.defaultDirection = this.table.sortOrder || 1;
    this.previousValue = this.table.value;
    const values = this.urlState.read(this.definitions());
    if (this.table.paginator) {
      this.table.rows = Number(values[this.key('por-pagina')]) || 15;
      this.table.rowsPerPageOptions = [15, 50, 100];
      this.table.first = ((values[this.key('pagina')] || 1) - 1) * this.table.rows;
    }
    const field = values[this.key('ordenar')];
    if (field) this.table.sortField = this.urlSortFields[field];
    if (field || values[this.key('direcao')]) this.table.sortOrder = values[this.key('direcao')] === 'desc' ? -1 : 1;
    this.subscriptions.add(this.table.onPage.subscribe(() => this.sync()));
    this.subscriptions.add(this.table.onSort.subscribe(() => this.sync()));
    Promise.resolve().then(() => this.restoring = false);
  }

  ngDoCheck(): void {
    const value = this.table.value;
    if (value === this.previousValue || !Array.isArray(value) || !this.table.paginator) return;
    this.previousValue = value;
    const lastPage = Math.max(1, Math.ceil(value.length / (this.table.rows || 15)));
    const page = Math.floor((this.table.first || 0) / (this.table.rows || 15)) + 1;
    if (page > lastPage) Promise.resolve().then(() => {
      if (this.destroyed) return;
      this.table.first = (lastPage - 1) * (this.table.rows || 15);
      this.sync();
    });
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.subscriptions.unsubscribe();
  }

  private key(name: string): string {
    return this.ssTableUrlState ? `${name}-${this.ssTableUrlState}` : name;
  }

  private definitions(): UrlParamDefinition[] {
    const defaultSlug = Object.keys(this.urlSortFields).find((key) => this.urlSortFields[key] === this.defaultField);
    return [
      { key: this.key('pagina'), type: 'number', minValue: 1, defaultValue: 1 },
      { key: this.key('por-pagina'), type: 'enum', options: ['15', '50', '100'], defaultValue: 15 },
      { key: this.key('ordenar'), type: 'enum', options: Object.keys(this.urlSortFields), defaultValue: defaultSlug },
      { key: this.key('direcao'), type: 'enum', options: ['asc', 'desc'], defaultValue: this.table.sortField === this.defaultField && this.defaultDirection === -1 ? 'desc' : 'asc' }
    ];
  }

  private sync(): void {
    if (this.restoring || this.destroyed) return;
    const slug = Object.keys(this.urlSortFields).find((key) => this.urlSortFields[key] === this.table.sortField);
    this.urlState.write(this.definitions(), {
      [this.key('pagina')]: this.table.paginator ? Math.floor((this.table.first || 0) / (this.table.rows || 15)) + 1 : null,
      [this.key('por-pagina')]: this.table.paginator ? this.table.rows : null,
      [this.key('ordenar')]: slug,
      [this.key('direcao')]: slug ? this.table.sortOrder === -1 ? 'desc' : 'asc' : null
    });
  }
}
